import React, { useEffect, useState } from "react";
import api from "../api/axios";
import {
    defaultConsent,
    getCookieConsent,
    saveCookieConsent,
    getAnonymousId,
} from "../utils/cookieConsent";
import {
    initGA,
    updateConsent,
} from "../analytics";
import "../assets/css/CookieConsentPopup.css";
import { Link } from "react-router-dom";

const CookieConsentPopup = () => {
    const [visible, setVisible] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [consent, setConsent] = useState(defaultConsent);

    useEffect(() => {
        getAnonymousId();

        const savedConsent = getCookieConsent();

        if (!savedConsent) {
            setVisible(true);
        } else {
            setConsent(savedConsent);

            if (savedConsent.analytics) {
                initGA();
            }
        }
    }, []);

    const saveConsentToBackend = async (finalConsent) => {
        try {
            await api.post(`/cookieConsentRoutes/cookie_consent`, {
                anonymous_id: finalConsent.anonymous_id,
                necessary: true,
                analytics: finalConsent.analytics,
                marketing: finalConsent.marketing,
                preferences: finalConsent.preferences,
                consent_version: finalConsent.version,
                given_at: finalConsent.givenAt,
            });
        } catch (error) {
            console.warn("Cookie consent backend save failed:", error);
        }
    };

    const handleSave = async (newConsent) => {
        const finalConsent = saveCookieConsent(newConsent);

        setConsent(finalConsent);
        setVisible(false);

        initGA();

        updateConsent({
            analytics: finalConsent.analytics,
            marketing: finalConsent.marketing,
        });

        await saveConsentToBackend(finalConsent);
    };

    const handleAcceptAll = () => {
        handleSave({
            analytics: true,
            marketing: true,
            preferences: true,
        });
    };

    const handleRejectOptional = () => {
        handleSave({
            analytics: false,
            marketing: false,
            preferences: false,
        });
    };

    const handleSaveChoices = () => {
        handleSave(consent);
    };

    if (!visible) return null;

    return (
        <div className="cookie-consent-overlay">
            <div className="cookie-consent-box">
                <div className="cookie-consent-content">
                    <h4>We value your privacy</h4>

                    <p>
                        We use necessary cookies to keep your account, cart, checkout and
                        website security working. With your consent, we also use cookies to enhance your browsing experience, serve personalised ads or content, and analyse our traffic. By clicking "Accept All", you consent to our use of cookies.
                       
                    </p>
                    <p>

                        You can read our{" "}
                        <Link to="/cookie-policy" className="cookie-link text-pink">
                            Cookie Policy
                        </Link>
                        ,{" "}
                        <Link to="/privacy-policy" className="cookie-link text-pink">
                            Privacy Policy
                        </Link>
                        ,{" "}
                        <Link to="/terms-and-condition" className="cookie-link text-pink">
                            Terms & Conditions
                        </Link>
                        , and{" "}
                        <Link to="/refund-policy" className="cookie-link text-pink">
                            Refund Policy
                        </Link>
                        .

                    </p>

                    {showSettings && (
                        <div className="cookie-settings">
                            <label className="cookie-option">
                                <input type="checkbox" checked disabled />
                                <span>
                                    <strong>Necessary cookies</strong>
                                    <small>
                                        Required for login, cart, checkout, authentication and
                                        security.
                                    </small>
                                </span>
                            </label>

                            <label className="cookie-option">
                                <input
                                    type="checkbox"
                                    checked={consent.analytics}
                                    onChange={(e) =>
                                        setConsent({
                                            ...consent,
                                            analytics: e.target.checked,
                                        })
                                    }
                                />
                                <span>
                                    <strong>Analytics cookies</strong>
                                    <small>
                                        Helps us understand page visits, traffic and website
                                        performance.
                                    </small>
                                </span>
                            </label>

                            <label className="cookie-option">
                                <input
                                    type="checkbox"
                                    checked={consent.marketing}
                                    onChange={(e) =>
                                        setConsent({
                                            ...consent,
                                            marketing: e.target.checked,
                                        })
                                    }
                                />
                                <span>
                                    <strong>Marketing cookies</strong>
                                    <small>
                                        Used for ads, retargeting and campaign performance.
                                    </small>
                                </span>
                            </label>

                            <label className="cookie-option">
                                <input
                                    type="checkbox"
                                    checked={consent.preferences}
                                    onChange={(e) =>
                                        setConsent({
                                            ...consent,
                                            preferences: e.target.checked,
                                        })
                                    }
                                />
                                <span>
                                    <strong>Preference cookies</strong>
                                    <small>
                                        Helps remember your selected settings and preferences.
                                    </small>
                                </span>
                            </label>
                        </div>
                    )}

                    <div className="cookie-actions">
                        <button
                            type="button"
                            className="cookie-btn secondary"
                            onClick={() => setShowSettings(!showSettings)}
                        >
                            {showSettings ? "Hide preferences" : "Manage preferences"}
                        </button>

                        <button
                            type="button"
                            className="cookie-btn secondary"
                            onClick={handleRejectOptional}
                        >
                            Reject optional
                        </button>

                        {showSettings ? (
                            <button
                                type="button"
                                className="cookie-btn custbuttonpink"
                                onClick={handleSaveChoices}
                            >
                                Save choices
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="cookie-btn custbuttonpink"
                                onClick={handleAcceptAll}
                            >
                                Accept all
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CookieConsentPopup;